// The seam between rendering (heroView/hud/input/mapView) and where the game state actually comes from:
// a local `Game` stepping in the browser, or a server snapshot stream. Every render module that must work
// both ways (2b's whole point) takes a `GameView`, never the concrete core `Game`/`Hero` — that is what
// lets the *same* heroView.driveHero/hud.createHud/input.createInput run unmodified against either.
//
// `ViewHero` is deliberately a plain SUBSET of core's own `Hero` fields, same names, same types — not an
// invented shape. That is what makes `LocalGameView` nearly free (a real `Hero` already satisfies
// `ViewHero` structurally, so wrapping never copies data) and what keeps every existing fake-`Hero` test
// fixture (see test/client/input.test.ts) working unchanged: a value typed `Hero` is still assignable
// wherever `ViewHero` is expected. Anything that isn't a raw field the renderer needs (whether a hero is
// currently walking, a charge-skill's fill level) is a `GameView` *method* instead of a `ViewHero` field,
// precisely so `ViewHero` never grows fields core's real `Hero` doesn't have — the moment it did, a real
// `Hero` would stop satisfying it and every existing offline fixture would need rewriting for a purely
// server-side concern.
import * as THREE from 'three';
import { Game, MOBILE_STATES, RULES, type Hero, type HeroState, type Zone, type Trap, type Barrier, type Reveal, type Projectile, type Lob } from '../core/game.js';
import type { Totem } from '../core/artifacts.js';
import { chargeLevel as coreChargeLevel } from '../core/skills.js';
import { ARTIFACTS, hasArtifact, canCastArtifact as coreCanCastArtifact, maxHpOf as coreMaxHpOf } from '../core/artifacts.js';
import type { HeroDef, HeroKind } from '../core/heroes.js';
import { matchStats as coreMatchStats, type MatchStats } from '../core/stats.js';
import { LAYOUT } from '../core/layout.js';
import type { Team, VisionSource } from '../core/layout.js';
import type { Command, SkillKey, ArtifactSlotKey, ArtifactLine, RelicLine } from '../core/commands.js';
import { canCastRelic as coreCanCastRelic, eventOnlyFor } from '../core/relics.js';
import type { GameEvent, HeroId } from '../core/events.js';
import type { ArtifactSlotView, YouEffects } from '../shared/protocol.js';
import { MONSTERS, monsterVisibleTo, type MonsterKind, type MonsterState } from '../core/monsters.js';
import { summonVisibleTo, type SummonKind } from '../core/summons.js';
import { endSeenBy, otherEnd } from '../core/bind.js';

export interface ViewHero {
  readonly id: HeroId;
  readonly kind: HeroKind;
  readonly def: HeroDef;
  readonly team: Team;
  readonly player: boolean;
  readonly pos: THREE.Vector3;
  readonly yaw: number;
  readonly lift: number;
  readonly hp: number;
  readonly alive: boolean;
  readonly respawnAt: number;
  readonly state: HeroState;
  readonly stunUntil: number;
  readonly cd: { attack: number; Q: number; W: number };
  /** Procedural stand-in's swing-arm timing (heroView.ts) — cosmetic, only matters while a glTF model
      hasn't loaded yet, so a remote hero that has no server-tracked value for it just never swings there. */
  readonly swingAt: number;
  /** E/R loadout — line+tier only, the same "visible to whoever can see the hero" field the wire sends
      (protocol.ts's `ArtifactSlotView`); a real core `ArtifactSlot` (line+tier+invested+cd) satisfies this
      structurally, so LocalGameView's zero-copy trick is unaffected. The matching cooldown is deliberately
      NOT here — same reasoning as `cd` above never growing an E/R key — see `artifactCdFor` below. */
  readonly artifacts: { E: ArtifactSlotView | null; R: ArtifactSlotView | null };
}

export type ViewPoint = { id: string; pos: { x: number; z: number }; owner: Team | null; progress: number; contested: boolean };

/** The viewer's own player-private economy state the shop UI needs beyond a hero's own E/R loadout
    (already on ViewHero): the TEAM's shared ink pool, whether standing in their own base, and this player's TEAM's active 45s
    line locks — protocol.ts's `SnapshotMessage.you`, minus the cooldowns `artifactCdFor`/`chargeLevel`
    already expose their own way. Structurally identical to shop.ts's own `YouStats` (that file's header
    explains why it was written before this existed) — never a rival's business, same reasoning as
    `cd`/`artifactCdFor` never existing for a hero that isn't the viewer, so this sits on GameView itself,
    not ViewHero. `effects` (the own hero's timed slows/haste/cloak, protocol.ts's `YouEffects`) feeds the
    HUD's stats block; optional because a view that doesn't forward it just shows those stats unmodified. */
export type YouStats = {
  ink: number; inBase: boolean; artifactLocks: readonly { line: ArtifactLine; remaining: number }[];
  effects?: Pick<YouEffects, 'slows' | 'haste' | 'cloakFor' | 'stillFor' | 'quietFor'>;
  /** The own team's relic cooldown and open Око, seconds (protocol.ts's `you.relicCd`/`you.eyeFor`); absent = 0. */
  relicCd?: number;
  eyeFor?: number;
};

/** Persistent world entities beyond heroes/points — the fx.ts/GameView unification's own contract: each
    shape mirrors protocol.ts's matching `*Snapshot` type exactly (a zone's `left` seconds-remaining, not
    core's own `until` timestamp; same reasoning as `stunUntil`→`stunFor` already gets on `HeroSnapshot`),
    because that shape is already exactly what fx.ts needs to draw and what the wire already sends — the
    unification's whole point is one contract, not fx.ts learning a second one for the local case. Every
    array is PRE-FILTERED by the implementation to what the viewer may see (own team's always; an enemy's
    only if actually in sight; traps/reveals never for a non-owner at all, matching server/snapshot.ts's own
    rules exactly) — fx.ts iterates these directly and never calls a visibility check on an entity itself,
    the same way it already never re-checks a `HeroSnapshot`'s own `visible` flag once past `visibleTo()`. */
export type ViewPool = {
  id: number; pos: { x: number; z: number }; r: number; life: number; left: number;
  look?: string; heal?: number; dmg?: number; slow?: number; immune?: boolean; reveal?: boolean;
};
export type ViewTrap = { id: number; pos: { x: number; z: number }; r: number; armIn: number; left: number };
export type ViewBarrier = { id: number; center: { x: number; z: number }; dir: { x: number; z: number }; half: number; team: Team; left: number };
export type ViewReveal = { id: number; x: number; z: number; r: number; left: number };
export type ViewTotem = { id: number; kind: 'ward' | 'wrath'; pos: { x: number; z: number }; r: number; team: Team; hp: number; left: number };
/** `dir`/`targetPos`/`srcId` are OFFLINE-ONLY extras, not part of the wire (`ProjectileSnapshot` has none
    of them) — `LocalGameView` populates them from core's real, richer `Projectile`; `RemoteGameView` leaves
    them `undefined`, same as today's reduced online projectile visual already does (net/remoteEntities.ts's
    own header: "not its full per-look particle trail, which stays offline-only" — this predates the
    unification and is not something it fixes). fx.ts orients a projectile by its aim when these are present
    and by its own motion between frames when they are not (client/projectileHeading.ts). */
export type ViewProjectile = {
  id: number; pos: { x: number; z: number }; team: Team; color?: number; big?: boolean; boltLook?: string;
  dir?: { x: number; z: number }; targetPos?: { x: number; z: number }; srcId?: number;
  /** Сокол на перчатке's 0..1 dive-arc progress — ON the wire (`ProjectileSnapshot.arc`) unlike `dir`/
      `targetPos`/`srcId` above, so it's real online and in replays too; `fillProjectile` computes the same
      value from core's own `p.arc` offline, so all three paths agree. */
  arc?: number;
};
export type ViewLob = { id: number; from: { x: number; z: number } | null; to: { x: number; z: number } | null; r: number; t0: number; flight: number; team: Team };

/** A neutral monster the viewer can see right now — both implementations hand out only visible AND alive
    ones (a dead monster has no model to draw). Same object per `id` across reads, `pos` mutated in place,
    so a renderer can key its model by identity like it does a hero. `stateT` is seconds in `state`,
    `brandFor` seconds left on Клеймо (0 when not branded). */
export type ViewMonster = {
  id: number; kind: MonsterKind; pos: THREE.Vector3; yaw: number; hp: number; maxHp: number;
  alive: boolean; state: MonsterState; stateT: number; moving: boolean; brandFor: number;
};
/** A summoned creature the viewer can see (protocol.ts's SummonSnapshot), same object per `id` across reads,
    `pos` mutated in place. `left`/`life` its lifetime in seconds, `swingAgo` seconds since its last attack. */
export type ViewSummon = {
  id: number; kind: SummonKind; team: Team; owner: HeroId; pos: THREE.Vector3; yaw: number; hp: number; maxHp: number;
  left: number; life: number; moving: boolean; swingAgo: number; brandFor: number;
  /** Always true: a view lists only the living — here so a Unit can be asked `alive` whatever it is. */
  alive: true;
};

/** Seconds left on each monster buff per team, `null` when not held — both teams, public information. */
export type ViewTeamBuffs = Record<Team, Record<MonsterKind, number | null>>;

/** Windup/dash/lunge/lungeWindup telegraph and Devil's leap/Прыжок hop — the continuous, timed sub-states
    fx.ts's telegraphs() draws. A GameView method, not ViewHero fields: core's own `Dash`/`Charge` carry
    clock-relative internals (`Charge.t0`, `Dash.hit` as a `Set<Hero>`) a wire value cannot reproduce and
    fx.ts never actually needs — `chargeLevel()` above already exists for exactly `charge.k`'s fraction, so
    this only adds the two pieces nothing else on the interface yet covers (a dash's remaining reach, a
    hop's flight arc). `null` throughout means "not in that sub-state right now". */
export type HeroTelegraph = {
  dash: { dir: { x: number; z: number }; left: number } | null;
  hop: { from: { x: number; z: number }; to: { x: number; z: number }; dur: number; arc?: number } | null;
};

/** Every continuous per-tick status fx.ts's statuses()/deaths() draws, bundled into one call instead of
    ten — see this type's own fields for why each exists; `HeroSnapshot`'s own header (protocol.ts) is the
    canonical reasoning for the boolean-vs-countdown choice on each one, carried over unchanged here. A
    GameView method rather than ViewHero fields, for the reason `chargeLevel`/`movingOf` already are:
    every one of these is DERIVED (needs `t`, or aggregates an array/buff table), so a real core `Hero`
    cannot satisfy it as a raw field without this interface stopping being a subset of `Hero`'s own shape. */
export type HeroStatus = {
  slowed: boolean; shielded: boolean; hasted: boolean; enraged: boolean; hasHot: boolean;
  empowered: boolean; feintArmed: boolean; invulnerable: boolean; taunted: boolean; pulled: boolean;
  cloaked: boolean;
  brandFor: number;
  /** Seconds since death, null while alive — same clock-sync reasoning as everything else here. */
  deadFor: number | null;
  /** Липкое пламя's afterburn (core's own `Hero.burn`; online, HeroSnapshot's `burning`). */
  burning?: boolean;
  /* ---- Астрал (HeroSnapshot's own fields of the same names): optional like `burning`, absent = off ---- */
  /** Слиться — only ever true for a hero the viewer can see at all, i.e. her own team's (or a replay's full vision). */
  invisible?: boolean;
  /** Соблазн / Стая's fear: walked by the server, no orders, casts or attacks; `forcedFor` seconds left. */
  forced?: 'charm' | 'fear' | null;
  forcedFor?: number;
  /** In an enemy Наваждение the viewer's team can see. */
  confused?: boolean;
  /** Бессонница: an enemy Кошмар the viewer's team can see is near enough to deny this hero's healing. */
  sleepless?: boolean;
  /** Без отражения: Тень stood still into a silhouette — seen by both teams, beyond every hit. */
  silhouette?: boolean;
  /* ---- Заклинатели ---- */
  /** Обернуться: Волчица in wolf form. */
  wolf?: boolean;
  /** Связка: tied — and to what spot, when the viewer's team sees the other end there (null otherwise). */
  tethered?: boolean;
  tetherTo?: { x: number; z: number } | null;
};

export interface GameView {
  readonly heroes: readonly ViewHero[];
  readonly points: readonly ViewPoint[];
  readonly pools: readonly ViewPool[];
  readonly traps: readonly ViewTrap[];
  readonly barriers: readonly ViewBarrier[];
  readonly reveals: readonly ViewReveal[];
  readonly totems: readonly ViewTotem[];
  readonly projectiles: readonly ViewProjectile[];
  readonly lobs: readonly ViewLob[];
  /** See ViewMonster's own header. */
  readonly monsters: readonly ViewMonster[];
  /** See ViewSummon's own header. */
  readonly summons: readonly ViewSummon[];
  readonly teamBuffs: ViewTeamBuffs;
  /** The relic each team holds (null: none) — public, like `teamBuffs`: the shop and the holders' plates read it. */
  readonly relics: Readonly<Record<Team, RelicLine | null>>;
  readonly events: readonly GameEvent[];
  readonly t: number;
  readonly clock: number;
  /** Seconds of pre-match countdown left, 0 once play has started. While it runs the game is not stepped
      and `apply()` drops every command; the camera still works. */
  readonly countdown: number;
  readonly score: { a: number; b: number };
  readonly overtime: boolean;
  readonly winner: Team | null;
  readonly winnerT: number;
  /** End-of-match stats, null until the match is over and they are known (online: until `matchEnd`). */
  readonly matchStats: MatchStats | null;
  readonly player: ViewHero | undefined;
  /** See YouStats's own header. */
  readonly you: YouStats;
  apply(heroId: HeroId, cmd: Command): void;
  /** Drains `events` after this frame's consumers (driveAnims, hud, fx/sound) have all read it once —
      same single end-of-frame clear the offline loop always did, now behind the seam so a remote view
      can apply it too and not replay one snapshot's events on every render frame until the next arrives. */
  clearEvents(): void;
  visibleTo(team: Team, h: ViewHero): boolean;
  canCast(h: ViewHero, key: SkillKey): boolean;
  /** input.ts's E/R gate — core's own `canCastArtifact` offline; online this is the same kind of cosmetic
      approximation `canCast` already is (real gate is server-side), `false` for any hero but the viewer's
      own (a rival's exact castability, like their cooldown, is never something this client can honestly
      answer). `false` for an empty slot either way. */
  canCastArtifact(h: ViewHero, slot: ArtifactSlotKey): boolean;
  /** Seconds remaining on slot `slot`'s cooldown, own hero only (0 for anyone else, matching `cd`'s own
      cooldown fields never existing for a hero that isn't the viewer) — a GameView method, not a ViewHero
      field, for the same reason `chargeLevel` is one: it would otherwise mean ViewHero growing an E/R key
      real core `Hero.cd` doesn't have (core's own artifact cooldown lives on the SLOT, `Hero.artifacts
      [slot].cd`, not on `Hero.cd`). */
  artifactCdFor(h: ViewHero, slot: ArtifactSlotKey): number;
  /** input.ts's T gate — core's `canCastRelic` offline, the same cosmetic approximation as `canCastArtifact`
      online, `false` for anyone but the viewer's own hero. */
  canCastRelic(h: ViewHero): boolean;
  /** 0..1 fill for a charge-style skill (arquebusier's Q); 0 when not mid-charge. */
  chargeLevel(h: ViewHero): number;
  /** True max hp, including a held Наплечник's flat bonus (core's own `maxHpOf`) — a GameView method, not
      a ViewHero field, for the same reason `chargeLevel` is one: it's derived (`HEROES[kind].hp` plus an
      artefact check), so a real core `Hero` has no matching raw field to satisfy it with. Anything drawing
      hp as a fraction (a health bar, a death-drain curve) must divide by this, never by `h.def.hp` alone,
      or it silently misreports for anyone holding the passive. */
  maxHpOf(h: ViewHero): number;
  /** Is `h` currently walking a path — the same gate driveHero's base-clip choice (Run vs Idle) needs. */
  movingOf(h: ViewHero): boolean;
  /** See HeroTelegraph's own header. */
  telegraphOf(h: ViewHero): HeroTelegraph;
  /** See HeroStatus's own header. */
  statusOf(h: ViewHero): HeroStatus;
  /** Basic-attack reach including Сошка (core's own `Game.attackRangeOf`). Online only the viewer's own
      hero can be answered (its `you.effects.stillFor`); anyone else reads `def.range`. */
  attackRangeOf(h: ViewHero): number;
  /** The terrain's walking-speed factor at a position (a ford's slow): core's `Game.groundSpeed`. */
  groundSpeedAt(x: number, z: number): number;
  /** The hero causing `h`'s taunt/pull, for fx.ts's tether-line visual — only ever answerable when the
      caller can already see both ends honestly: `LocalGameView` always can (core holds the real ref);
      `RemoteGameView` never can (protocol.ts's HeroSnapshot deliberately never sends `taunt.by`/`pull.by` —
      see its own header on why that specific pair would leak fog no other field here does) and always
      returns `null`, same as taunted/pulled staying plain booleans on the wire. fx.ts falls back to a bare
      presence marker (no tether line) whenever this is `null`, never fabricates an endpoint. */
  tetherSource(h: ViewHero): ViewHero | null;
  /** Cosmetic position-fog check for a one-shot event that isn't tied to any one entity already gated by
      an array above (fx.ts's own dispatch: a `pos`-carrying event needs this, a `hero`-carrying one uses
      `visibleTo` instead). Online this duplicates a filter the server already applied to `events` itself —
      harmless, since both sides answer the same question the same way, and it keeps fx.ts's dispatch one
      piece of logic instead of two (an offline-only filter plus an online-only "trust it"). */
  inSight(team: Team, pos: { x: number; z: number }): boolean;
  /** The fog-of-war shader's soft-edged sight shadow (main.ts's drawVision) — cosmetic dimming layered
      on top of the real anti-cheat line (which heroes get sent a position at all, never this). Only ever
      called with the viewer's own team, so a remote view can answer it exactly: its own base position
      (static, public) plus its own team's visible heroes and reveals is precisely core's own formula. */
  visionSources(team: Team): VisionSource[];
}

/** Stable per-object ids for entity kinds core itself never numbered (a `Zone`/`Trap`/`Barrier`/`Reveal`/
    `Projectile`/`Lob` has no `id` field — only `Totem` does, core's own artefact code assigns it one
    already). Same WeakMap trick server/snapshot.ts's own `idOf()` uses, independently: both need "a stable
    number for a stable object, for as long as that exact object lives", and this is the client-side half
    of it — there is no core Game to share the implementation from, since it must not know clients want to
    diff it across frames at all. */
const idOf = (() => {
  const ids = new WeakMap<object, number>();
  let next = 1;
  return (o: object): number => {
    let id = ids.get(o);
    if (id === undefined) { id = next++; ids.set(o, id); }
    return id;
  };
})();

/** Returns the SAME `View*` object for the same underlying core object across calls, its fields
    reassigned to `fresh` in place — not a fresh literal every read. fx.ts's `visualFor(owner, build)`
    caches a mesh by `owner`'s identity (so a persistent entity's mesh is built once and updated, never
    torn down and rebuilt every snapshot — the very thing this whole unification asks entities to keep
    doing); a getter returning a new plain object every call would silently defeat that cache AND leak one
    orphaned mesh into `scene` per entity per frame, since the OLD returned object stops being reachable
    the instant the getter is read again. Identity is the product here, not the values — `fresh` is spread
    onto the cached object every call precisely so the returned reference never changes while the same
    core object is still alive. `fill` rewrites every field (nested {x,z} included) in place, so a read
    allocates nothing once the view object exists. */
function memoView<S extends object, V extends object>(cache: WeakMap<S, V>, src: S, make: () => V, fill: (v: V, s: S) => void): V {
  let v = cache.get(src);
  if (!v) { v = make(); cache.set(src, v); }
  fill(v, src);
  return v;
}
const xz = () => ({ x: 0, z: 0 });
const setXz = (o: { x: number; z: number }, p: { x: number; z: number }) => { o.x = p.x; o.z = p.z; return o; };

const newPool = (): ViewPool => ({ id: 0, pos: xz(), r: 0, life: 0, left: 0 });
const newTrap = (): ViewTrap => ({ id: 0, pos: xz(), r: 0, armIn: 0, left: 0 });
const newBarrier = (): ViewBarrier => ({ id: 0, center: xz(), dir: xz(), half: 0, team: 'a', left: 0 });
const newReveal = (): ViewReveal => ({ id: 0, x: 0, z: 0, r: 0, left: 0 });
const newTotem = (): ViewTotem => ({ id: 0, kind: 'ward', pos: xz(), r: 0, team: 'a', hp: 0, left: 0 });
const newProjectile = (): ViewProjectile => ({ id: 0, pos: xz(), team: 'a' });
function fillProjectile(v: ViewProjectile, p: Projectile) {
  v.id = idOf(p); setXz(v.pos, p.pos); v.team = p.src.team; v.color = p.color; v.big = p.big;
  v.boltLook = p.look ?? (p.kind === 'linear' ? 'lance' : p.src.def.boltLook);
  v.dir = p.kind === 'linear' ? setXz(v.dir ?? xz(), p.dir!) : undefined;
  v.targetPos = p.target ? setXz(v.targetPos ?? xz(), p.target.pos) : undefined;
  v.srcId = p.src.id;
  v.arc = p.arc;
}
const newLob = (): ViewLob => ({ id: 0, from: xz(), to: xz(), r: 0, t0: 0, flight: 0, team: 'a' });
function fillLob(v: ViewLob, l: Lob) {
  v.id = idOf(l); v.from = setXz(v.from ?? xz(), l.from); v.to = setXz(v.to ?? xz(), l.to);
  v.r = l.r; v.t0 = l.t0; v.flight = l.flight; v.team = l.src.team;
}

/** Thin, all-forwarding wrapper: offline play stays exactly what it always was, just behind the seam
    online play needs too. Every `ViewHero` handed out here IS the real `Hero` object (Hero satisfies
    ViewHero already), so identity-keyed state elsewhere (fx.ts's WeakMaps, heroView's rig lookup by id)
    is completely unaffected — this never copies a hero, only narrows what callers are allowed to see. The
    new entity/status getters below are the one place this stops being free: each maps a handful of core
    objects into their wire-shaped `View*` form every read (`left` instead of `until`, `k` instead of
    `t0`, a stable `id` instead of object identity) — unavoidable for the same reason `HeroSnapshot` itself
    needed the conversion, and cheap at this game's entity counts (single digits to low tens). */
export class LocalGameView implements GameView {
  /** `names[heroId]`: a human's display name for the results screen, null/missing for a bot. */
  constructor(private readonly game: Game, private readonly names: readonly (string | null)[] = []) {}

  /** Run down by main.ts's stepSim, which does not step the game until it reaches 0. */
  countdown = 0;

  get heroes(): readonly ViewHero[] { return this.game.heroes; }
  // Same zero-copy trick as `heroes`: core's own Point3 (id/pos/owner/progress/contested, `pos` a
  // THREE.Vector3) is already a structural superset of ViewPoint (`pos` only needs {x,z}, which Vector3
  // has) — this used to map a fresh array of fresh objects on every read, three readers per frame
  // (matchView.ts's syncPoints/hud.ts/minimap), for ~720 throwaway objects/s that a straight pass-through
  // makes unnecessary.
  get points(): readonly ViewPoint[] { return this.game.points; }

  /* Own-team always; an enemy's only if actually in sight — core's own `visibleAt` rule (duplicated here,
     not imported: it is three lines of Game's own public inSight()/pos, not worth a shared export for),
     same as server/snapshot.ts applies for the online case. A `follow` pool (Ferryman's W) is gated by its
     OWNER's visibility instead — `inSight` alone would leak the owner's exact position through brush that
     is hiding them, exactly like the wire's own comment on this same case. */
  private visibleAt(entityTeam: Team, p: { x: number; z: number }): boolean {
    const team = this.game.player!.team;
    return entityTeam === team || this.game.inSight(team, p);
  }
  private readonly poolViews = new WeakMap<object, ViewPool>();
  get pools(): readonly ViewPool[] {
    const team = this.game.player!.team;
    return this.game.pools
      .filter(z => z.follow ? this.game.visibleTo(team, z.follow) : this.visibleAt(z.team, z.pos))
      // Тень на стене's marker hides with her, as server/snapshot.ts's seesPool does.
      .filter(z => !(z.hideWithSrc && z.team !== team && this.game.stealthed(z.src) && !this.game.eyeOpen(team)))
      .map(z => memoView(this.poolViews, z, newPool, this.fillPool));
  }
  private readonly fillPool = (v: ViewPool, z: Zone) => {
    v.id = idOf(z); setXz(v.pos, z.pos); v.r = z.r; v.life = z.life; v.left = Math.max(0, z.until - this.game.t);
    v.look = z.look; v.heal = z.heal; v.dmg = z.dmg; v.slow = z.slow; v.immune = z.immune; v.reveal = z.reveal;
  };
  // Offline's own long-standing rule (predates this unification, kept exactly for "prove it unchanged"):
  // own traps always, an enemy one only once a teammate is standing close enough to have noticed it
  // (`tr.sight`) — a single-player-vs-bots courtesy, not an anti-cheat boundary, since there is no second
  // process to leak to here. This is DELIBERATELY more permissive than the wire's own rule (never sent to
  // the enemy while armed, full stop, regardless of proximity — see server/snapshot.ts) — a known,
  // accepted asymmetry between the two modes, not something to unify away; see the fx.ts unification's own
  // parity report for why armed enemy traps online never show even from a step away.
  private readonly trapViews = new WeakMap<object, ViewTrap>();
  get traps(): readonly ViewTrap[] {
    const team = this.game.player!.team;
    return this.game.traps
      .filter(t => t.team === team || this.game.heroes.some(h => h.alive && h.team === team && Math.hypot(h.pos.x - t.pos.x, h.pos.z - t.pos.z) < t.sight!))
      .map(t => memoView(this.trapViews, t, newTrap, this.fillTrap));
  }
  private readonly fillTrap = (v: ViewTrap, t: Trap) => {
    v.id = idOf(t); setXz(v.pos, t.pos); v.r = t.r; v.armIn = Math.max(0, t.armAt - this.game.t); v.left = Math.max(0, t.until - this.game.t);
  };
  // Offline has never gated this one at all — a Павеза board is drawn for both teams unconditionally, kept
  // exactly that way here ("prove it unchanged"); online's own filter (visibleAt, same as pools/projectiles)
  // is the stricter, anti-cheat-correct rule and lives only in server/snapshot.ts, a known asymmetry — see
  // this unification's own parity report.
  private readonly barrierViews = new WeakMap<object, ViewBarrier>();
  get barriers(): readonly ViewBarrier[] {
    return this.game.barriers
      .map(b => memoView(this.barrierViews, b, newBarrier, this.fillBarrier));
  }
  private readonly fillBarrier = (v: ViewBarrier, b: Barrier) => {
    v.id = idOf(b); setXz(v.center, b.center); setXz(v.dir, b.dir); v.half = b.half; v.team = b.team; v.left = Math.max(0, b.until - this.game.t);
  };
  // Never sent to the team that doesn't own it — a reveal grants ITS OWNER's vision, so showing its
  // position to the opposing team leaks exactly what fog-of-war exists to hide.
  private readonly revealViews = new WeakMap<object, ViewReveal>();
  get reveals(): readonly ViewReveal[] {
    const team = this.game.player!.team;
    return this.game.reveals.filter(r => r.team === team)
      .map(r => memoView(this.revealViews, r, newReveal, this.fillReveal));
  }
  private readonly fillReveal = (v: ViewReveal, r: Reveal) => {
    v.id = idOf(r); v.x = r.x; v.z = r.z; v.r = r.r; v.left = Math.max(0, r.until - this.game.t);
  };
  private readonly totemViews = new WeakMap<object, ViewTotem>();
  get totems(): readonly ViewTotem[] {
    const team = this.game.player!.team;
    return this.game.totems.filter(b => this.game.totemVisibleTo(team, b))
      .map(b => memoView(this.totemViews, b, newTotem, this.fillTotem));
  }
  private readonly fillTotem = (v: ViewTotem, b: Totem) => {
    v.id = b.id; v.kind = b.kind; setXz(v.pos, b.pos); v.r = b.r; v.team = b.team; v.hp = b.hp; v.left = Math.max(0, b.until - this.game.t);
  };
  private readonly projectileViews = new WeakMap<object, ViewProjectile>();
  get projectiles(): readonly ViewProjectile[] {
    return this.game.projectiles.filter(p => p.live && this.visibleAt(p.src.team, p.pos))
      .map(p => memoView(this.projectileViews, p, newProjectile, fillProjectile));
  }
  // Offline has never gated this one either — both endpoints are drawn unconditionally, kept exactly that
  // way here; online's own independent from/to fog gating (protocol.ts's LobSnapshot) is the stricter,
  // anti-cheat-correct rule and lives only in server/snapshot.ts — see this unification's own parity report.
  private readonly lobViews = new WeakMap<object, ViewLob>();
  get lobs(): readonly ViewLob[] {
    return this.game.lobs.filter(l => l.live)
      .map(l => memoView(this.lobViews, l, newLob, fillLob));
  }

  private readonly monsterViews = new Map<number, ViewMonster>();
  get monsters(): readonly ViewMonster[] {
    const team = this.game.player!.team;
    return this.game.monsters.filter(m => m.alive && monsterVisibleTo(this.game, team, m)).map(m => {
      let v = this.monsterViews.get(m.id);
      if (!v) { v = { id: m.id, kind: m.kind, pos: new THREE.Vector3(), yaw: 0, hp: 0, maxHp: 0, alive: true, state: 'idle', stateT: 0, moving: false, brandFor: 0 }; this.monsterViews.set(m.id, v); }
      v.kind = m.kind; v.pos.set(m.pos.x, 0, m.pos.z); v.yaw = m.yaw; v.hp = m.hp; v.maxHp = MONSTERS[m.kind].hp;
      v.alive = m.alive; v.state = m.state; v.stateT = m.stateT;
      v.brandFor = Math.max(0, ...m.marks.map(k => k.until - this.game.t));
      v.moving = m.path.length > 0 && (m.state === 'wander' || m.state === 'chase' || m.state === 'return');
      return v;
    });
  }
  private readonly summonViews = new Map<number, ViewSummon>();
  get summons(): readonly ViewSummon[] {
    const g = this.game, team = g.player!.team;
    const seen = g.summons.filter(s => s.alive && summonVisibleTo(g, team, s));
    if (this.summonViews.size > seen.length) for (const id of this.summonViews.keys()) if (!seen.some(s => s.id === id)) this.summonViews.delete(id);
    return seen.map(s => {
      let v = this.summonViews.get(s.id);
      if (!v) { v = { id: s.id, kind: s.kind, team: s.team, owner: s.owner.id, pos: new THREE.Vector3(), yaw: 0, hp: 0, maxHp: 0, left: 0, life: 0, moving: false, swingAgo: 9, brandFor: 0, alive: true }; this.summonViews.set(s.id, v); }
      v.pos.set(s.pos.x, 0, s.pos.z); v.yaw = s.yaw; v.hp = Math.max(0, s.hp); v.maxHp = s.maxHp;
      v.left = Math.max(0, s.until - g.t); v.life = s.life; v.moving = s.path.length > 0; v.swingAgo = Math.min(9, g.t - s.swingAt);
      v.brandFor = Math.max(0, ...s.marks.map(k => k.until - g.t));
      return v;
    });
  }
  get teamBuffs(): ViewTeamBuffs {
    const left = (until: number | null) => (until === null ? null : Math.max(0, until - this.game.t));
    const of = (team: Team) => ({ behemoth: left(this.game.teamBuffs[team].behemoth), griffin: left(this.game.teamBuffs[team].griffin) });
    return { a: of('a'), b: of('b') };
  }
  get relics() { return { a: this.game.relics.a?.line ?? null, b: this.game.relics.b?.line ?? null }; }

  /* Core's own events, less those that happen ON a hero the player's team can't see (a hit on Тень under
     Слиться, a trap going off under someone in grass) — the rule server/snapshot.ts's eventVisible applies
     through `subjectOf` too. The unfiltered array itself is handed out whenever nothing needs dropping. */
  get events(): readonly GameEvent[] {
    const all = this.game.events, team = this.game.player!.team;
    const hidden = (e: GameEvent) => {
      const only = eventOnlyFor(this.game, e);  // an Око cast: its own team's alone, as on the wire
      if (only) return only !== team;
      const s = this.game.subjectOf(e); return !!s && !this.game.subjectVisibleTo(team, s);
    };
    const unnamed = (e: GameEvent) => this.game.attackerHidden(team, e);  // a blow from someone unseen: no `by`
    if (!all.some(e => hidden(e) || unnamed(e))) return all;
    return all.filter(e => !hidden(e)).map(e => this.game.eventFor(team, e));
  }
  get t() { return this.game.t; }
  get clock() { return this.game.clock; }
  get score() { return this.game.score; }
  get overtime() { return this.game.overtime; }
  get winner() { return this.game.winner; }
  get winnerT() { return this.game.winnerT; }
  get matchStats(): MatchStats | null { return this.game.winner ? coreMatchStats(this.game, this.names) : null; }
  get player(): ViewHero | undefined { return this.game.player; }
  get you(): YouStats {
    const h = this.game.player!, [bx, bz] = LAYOUT.bases[h.team], t = this.game.t;
    return {
      ink: this.game.ink[h.team],
      inBase: h.alive && Math.hypot(h.pos.x - bx, h.pos.z - bz) < RULES.fountainRadius,
      artifactLocks: this.game.artifactLocks.filter(l => l.team === h.team).map(l => ({ line: l.line, remaining: Math.max(0, l.until - this.game.t) })),
      // Same shape and filters as server/snapshot.ts's buildYou().effects.
      effects: {
        slows: h.slows.filter(s => s.until > t).map(s => ({ pct: s.pct, left: s.until - t })),
        haste: h.buffs.filter(b => b.type === 'haste' && b.until > t).map(b => ({ val: b.val, left: b.until - t })),
        cloakFor: Math.max(0, h.cloakUntil - t),
        ...(hasArtifact(h, 'boots') ? { quietFor: Math.min(ARTIFACTS.boots.quiet, t - h.lastDamageAt) } : {}),
      },
      relicCd: this.game.relics[h.team]?.cd ?? 0,
      eyeFor: Math.max(0, this.game.eyeUntil[h.team] - t),
    };
  }

  apply(heroId: HeroId, cmd: Command) { if (this.countdown <= 0) this.game.apply(heroId, cmd); }
  clearEvents() { this.game.events.length = 0; }
  visibleTo(team: Team, h: ViewHero) { return this.game.visibleTo(team, h as Hero); }
  canCast(h: ViewHero, key: SkillKey) { return this.game.canCast(h as Hero, key); }
  canCastArtifact(h: ViewHero, slot: ArtifactSlotKey) { return coreCanCastArtifact(this.game, h as Hero, slot); }
  artifactCdFor(h: ViewHero, slot: ArtifactSlotKey) { return (h as Hero).artifacts[slot]?.cd ?? 0; }
  canCastRelic(h: ViewHero) { return coreCanCastRelic(this.game, h as Hero); }
  chargeLevel(h: ViewHero) { return coreChargeLevel(this.game, h as Hero); }
  maxHpOf(h: ViewHero) { return coreMaxHpOf(h as Hero); }
  movingOf(h: ViewHero) {
    const real = h as Hero;
    return real.alive && real.path.length > 0 && !real.pending && MOBILE_STATES.has(real.state);
  }
  telegraphOf(h: ViewHero): HeroTelegraph {
    const real = h as Hero;
    return {
      dash: real.dash ? { dir: { x: real.dash.dir.x, z: real.dash.dir.z }, left: real.dash.left } : null,
      hop: real.hop ? { from: { x: real.hop.from.x, z: real.hop.from.z }, to: { x: real.hop.to.x, z: real.hop.to.z }, dur: real.hop.dur, arc: real.hop.arc } : null,
    };
  }
  statusOf(h: ViewHero): HeroStatus {
    const real = h as Hero, g = this.game, t = g.t;
    return {
      slowed: real.slows.some(s => s.until > t),
      shielded: !!real.shield && real.shield.until > t,
      hasted: !!g.buffVal(real, 'haste'),
      enraged: !!g.buffVal(real, 'dmg'),
      hasHot: real.hots.some(o => o.until > t),
      empowered: real.empowered,
      feintArmed: real.feintArmed,
      invulnerable: real.alive && t < real.invulnUntil,
      taunted: g.taunted(real),
      pulled: !!real.pull,
      cloaked: real.alive && t < real.cloakUntil,
      // `source` is always 'brand' now (Игла гравёра, `marks`'s only other producer, is gone) — no filter
      // needed, just the longest remaining mark, same as always.
      brandFor: Math.max(0, ...real.marks.map(m => m.until - t)),
      deadFor: real.alive ? null : t - (real.diedAt ?? t),
      burning: !!real.burn,
      invisible: g.stealthed(real),
      forced: real.alive ? g.forcedKind(real) : null,
      forcedFor: real.alive && real.forced ? Math.max(0, real.forced.until - t) : 0,
      // Same gate as server/snapshot.ts's seenCause: only while the viewer's team sees the Кошмар behind it.
      confused: real.alive && this.seenCause(g.confusedBy(real)),
      sleepless: real.alive && this.seenCause(g.sleeplessBy(real)),
      silhouette: g.silhouette(real),
      wolf: g.wolfForm(real),
      tethered: real.alive && !!real.tether,
      tetherTo: real.alive && real.tether ? this.tetherTo(real) : null,
    };
  }
  private tetherTo(h: Hero) {
    const end = otherEnd(h.tether!, h);
    return endSeenBy(this.game, this.game.player!.team, end) ? { x: end.pos.x, z: end.pos.z } : null;
  }
  private seenCause(cause: Hero | undefined) { return !!cause && this.game.visibleTo(this.game.player!.team, cause); }

  attackRangeOf(h: ViewHero): number { return this.game.attackRangeOf(h as Hero); }
  groundSpeedAt(x: number, z: number): number { return this.game.groundSpeed(x, z); }
  tetherSource(h: ViewHero): ViewHero | null {
    const real = h as Hero;
    return (real.taunt?.by ?? real.pull?.by) ?? null;
  }
  inSight(team: Team, pos: { x: number; z: number }) { return this.game.inSight(team, pos); }
  visionSources(team: Team) { return this.game.visionSources(team); }
}
